import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { useAuth } from '../context/auth-provider'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { Logo } from '../components/logo'
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google'

const LOGIN_WITH_GOOGLE_MUTATION = gql(`
  mutation LoginWithGoogle($idToken: String!) {
    loginWithGoogle(idToken: $idToken) {
      accessToken
      refreshToken
      user { id name email }
    }
  }
`)

const LOGIN_MUTATION = gql(`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      accessToken
      refreshToken
      user {
        id
        name
        email
      }
    }
  }
`)

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const { setAccessToken } = useAuth()
  const [{ fetching }, login] = useMutation(LOGIN_MUTATION)
  const [, loginWithGoogle] = useMutation(LOGIN_WITH_GOOGLE_MUTATION)
  const navigate = useNavigate()

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError('')
    const result = await loginWithGoogle({ idToken: credentialResponse.credential ?? '' })
    if (result.error) { setError(result.error.graphQLErrors[0]?.message ?? result.error.message); return }
    if (result.data?.loginWithGoogle) {
      setAccessToken(result.data.loginWithGoogle.accessToken)
      navigate('/dashboard')
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    
    const result = await login({ email, password })
    
    if (result.error) {
      setError(result.error.graphQLErrors[0]?.message ?? result.error.message)
      return
    }

    if (result.data?.login) {
      setAccessToken(result.data.login.accessToken)
      navigate('/dashboard')
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
      <div className="scale-150">
        <Logo />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Welcome back</CardTitle>
          <CardDescription>Enter your credentials to access your account</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="text-sm text-red-500 font-medium">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="name@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full" disabled={fetching}>
              {fetching ? 'Signing in...' : 'Sign in'}
            </Button>
            <div className="relative w-full flex items-center gap-2 py-1">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 border-t border-border" />
            </div>
            <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in failed')} width="100%" />
            <Button variant="link" size="sm" asChild>
              <Link to="/forgot-password">Forgot password?</Link>
            </Button>
            <Button variant="link" className="w-full" asChild>
              <Link to="/signup">Don't have an account? Sign up</Link>
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}